import React, { useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material'
import LinkIcon from '@mui/icons-material/Link'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

interface Props {
  onComplete: () => void
}

export default function ParentOnboarding({ onComplete }: Props) {
  const { session }           = useAuth()
  const [email,    setEmail]  = useState('')
  const [loading,  setLoading] = useState(false)
  const [error,    setError]  = useState<string | null>(null)

  async function handleLink(e: React.FormEvent) {
    e.preventDefault()
    if (!session) return
    setError(null)
    setLoading(true)

    try {
      // Linking runs server-side (link-parent-student): parents can't read
      // other users under RLS, and the edge function validates the target is a
      // real student before creating the link.
      const { data, error: fnErr } = await supabase.functions.invoke('link-parent-student', {
        body: { student_email: email.trim().toLowerCase() },
      })

      if (fnErr) {
        setError('Something went wrong. Please try again.')
        return
      }
      if (data && data.ok === false) {
        setError(data.error ?? 'No student account found with that email.')
        return
      }

      onComplete()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{
      minHeight:      '100vh',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      bgcolor:        'background.default',
      p:              3,
    }}>
      <Box sx={{ width: '100%', maxWidth: 420 }}>

        {/* Wordmark */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: 'primary.main', letterSpacing: '-0.01em', lineHeight: 1 }}>
            Ardent
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '0.6rem', color: 'secondary.main', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Scholar
          </Typography>
        </Box>

        {/* Card */}
        <Box sx={{
          bgcolor:      'background.paper',
          borderRadius: 3,
          border:       '1px solid',
          borderColor:  'divider',
          p:            4,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <LinkIcon sx={{ color: 'secondary.main', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Link your student</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
            Enter your student's email address to connect their account to your portal.
            You can link additional students later.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleLink} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Student email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ py: 1.25 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Link Student'}
            </Button>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center', lineHeight: 1.6 }}>
          Your student must have accepted their invitation before you can link them.
        </Typography>
      </Box>
    </Box>
  )
}
